import { useState } from 'react';
import { DillButton, DillModal, DillAvatar, DillInput, DillField } from '../../../../general/Dill';
import { Plus, Trash, Phone, Envelope } from 'phosphor-react';
import { useAppDispatch } from '../../../../../redux/store';
import { openMessageModal } from '../../../../../redux/globalSlices/genericSlice';
import { BuyerContact } from '@dill/dill-shared';

interface CRMContactsModalProps {
  open: boolean;
  buyerName: string;
  contacts: BuyerContact[];
  onAddContact: (contact: Omit<BuyerContact, 'id'>) => void;
  onDeleteContact: (contactId: string) => void;
  onClose: () => void;
}

const CRMContactsModal = ({
  open,
  buyerName,
  contacts,
  onAddContact,
  onDeleteContact,
  onClose,
}: CRMContactsModalProps) => {
  const dispatch = useAppDispatch();

  const [isAdding, setIsAdding] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const resetForm = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhoneNumber('');
    setErrors({});
  };

  const handleSave = () => {
    const newErrors: Record<string, string> = {};

    if (!firstName.trim() && !lastName.trim()) {
      newErrors.name = 'First name or last name is required';
    }
    if (!email.trim() && !phoneNumber.trim()) {
      newErrors.contact = 'Email or phone number is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onAddContact({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      phoneNumber: phoneNumber.trim(),
    });
    resetForm();
    setIsAdding(false);
  };

  if (!open) return null;

  return (
    <DillModal
      open
      onClose={onClose}
      title="CRM Contacts"
      subtitle={buyerName}
      maxWidth="760px"
      footer={<DillButton variant="secondary" label="Close" onClick={onClose} />}>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Envelope size={16} weight="duotone" className="text-blue-500" />
            <span className="text-xs font-bold text-gray-900">
              {contacts.length || 0} contact
              {contacts.length !== 1 ? 's' : ''}
            </span>
          </div>
          <DillButton
            label="Add Contact"
            variant="secondary"
            icon={<Plus size={14} />}
            height={28}
            onClick={() => {
              resetForm();
              setIsAdding(true);
            }}
          />
        </div>

        {isAdding && (
          <div className="border border-blue-200 bg-blue-50/50 rounded-lg p-3 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <DillField label="First Name" error={errors.name}>
                <DillInput
                  value={firstName}
                  onChange={setFirstName}
                  placeholder="First name"
                  error={errors.name}
                />
              </DillField>
              <DillField label="Last Name">
                <DillInput value={lastName} onChange={setLastName} placeholder="Last name" />
              </DillField>
              <DillField label="Email" error={errors.contact}>
                <DillInput
                  value={email}
                  onChange={setEmail}
                  placeholder="Email address"
                  error={errors.contact}
                />
              </DillField>
              <DillField label="Phone Number">
                <DillInput
                  value={phoneNumber}
                  onChange={setPhoneNumber}
                  placeholder="Phone number"
                />
              </DillField>
            </div>
            <div className="flex gap-2 justify-end">
              <DillButton
                variant="secondary"
                label="Cancel"
                height={28}
                onClick={() => {
                  resetForm();
                  setIsAdding(false);
                }}
              />
              <DillButton label="Save" height={28} onClick={handleSave} />
            </div>
          </div>
        )}

        {contacts.length > 0 && (
          <>
            <div className="grid grid-cols-[1fr_160px_160px_28px] gap-2 items-center px-2 pb-1">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Name
              </span>
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Email
              </span>
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Phone
              </span>
              <span />
            </div>
            <div className="space-y-1.5 max-h-[320px] overflow-y-auto">
              {contacts.map((contact, index) => {
                const displayName =
                  `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || 'No Name';
                return (
                  <div
                    key={contact.id || index}
                    className="grid grid-cols-[1fr_160px_160px_28px] gap-2 items-center p-2 rounded-lg border border-gray-50 hover:border-gray-200 transition-colors">
                    <div className="flex items-center gap-2 min-w-0">
                      <DillAvatar name={contact.firstName || contact.email || 'C'} idx={index} />
                      <p className="text-xs font-medium text-gray-900 truncate capitalize">
                        {displayName}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 min-w-0">
                      {contact.email && (
                        <>
                          <Envelope size={12} className="text-gray-400 flex-shrink-0" />
                          <p className="text-xs text-gray-600 truncate">{contact.email}</p>
                        </>
                      )}
                    </div>
                    <div className="flex items-center gap-1 min-w-0">
                      {contact.phoneNumber && (
                        <>
                          <Phone size={12} className="text-gray-400 flex-shrink-0" />
                          <p className="text-xs text-gray-600 truncate">{contact.phoneNumber}</p>
                        </>
                      )}
                    </div>
                    <div className="flex items-center justify-center">
                      <DillButton
                        variant="icon-red"
                        icon={<Trash size={16} weight="bold" />}
                        onClick={() =>
                          dispatch(
                            openMessageModal({
                              buttonText: 'Cancel',
                              isSecondButton: true,
                              secondButtonText: 'Yes, remove',
                              modalType: 'WARN',
                              title: 'Remove Contact',
                              subTitle: `Are you sure you want to remove ${displayName} from CRM contacts?`,
                              onSecondButtonClick: () => {
                                onDeleteContact(contact.id);
                              },
                            })
                          )
                        }
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {contacts.length === 0 && !isAdding && (
          <div className="text-center py-8 text-gray-400 text-sm">
            No CRM contacts yet. Click &quot;Add Contact&quot; to add one.
          </div>
        )}
      </div>
    </DillModal>
  );
};

export default CRMContactsModal;
